import { apiClient } from './api';

export interface PPEData {
  id: number;
  revenue: number; // 매출액
  laborCost?: number; // 투입인건비 (구 버전 호환)
  grossCost?: number; // Gross Cost
  manpowerCost?: number; // 투입인건비 (새 버전)
  indirectOpex?: number; // Indirect OPEX 총합
  directOpex?: number; // Direct OPEX 총합
  grossIncome: number; // Gross Income
  grossIncomeRate: number; // Gross Income %
  operationIncome: number; // Operation Income
  operationIncomeRate: number; // Operation Income %
  profit: number; // Profit
  profitRate: number; // Profit %
  months?: number; // 프로젝트 기간(월)

  // 추가 계산 데이터
  ecm?: number; // ECM
  costOfFinance?: number; // Cost of Finance
  costOfFinanceRate?: number; // Cost of Finance 비율 (기본값 0.1 = 10%)
  projectManagementRate?: number; // Project Management 비율 (기본값 0.03 = 3%)
  reserved?: number; // Reserved

  // JSON 필드들
  revenueItems?: any; // 매출 세부 항목
  grossCostItems?: any; // Gross Cost 세부 항목
  manpowerItems?: any; // 투입인건비 세부 항목
  indirectOpexItems?: any; // Indirect OPEX 세부 항목
  directOpexItems?: any; // Direct OPEX 세부 항목
  revenueByConsultantItems?: any; // Revenue by Consultant 세부 항목

  createdAt: string;
  updatedAt: string;
  project?: {
    id: number;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
    contractAmount: number;
    projectClient?: {
      id: number;
      companyName: string;
      contactPerson: string;
      contactNumber: string;
      email: string;
    };
    projectPayment?: {
      id: number;
      downPayment: number;
      middlePayment: number;
      finalPayment: number;
    };
    internalStaff?: {
      id: number;
      name: string;
      role: string;
      startDate: string;
      endDate: string;
      utilization: number;
      exclusionDays: number;
      totalCost: number;
    }[];
    externalStaff?: {
      id: number;
      name: string;
      role: string;
      contact: string;
      period: string;
      cost: number;
      memo: string;
    }[];
  };
}

export interface CreatePPEDto {
  // 기본 입력 데이터
  revenue: number; // 매출액
  grossCost: number; // Gross Cost
  manpowerCost: number; // 투입인건비
  indirectOpex: number; // Indirect OPEX
  directOpex: number; // Direct OPEX

  // 계산된 데이터
  grossMargin?: number; // Gross Margin (revenue - grossCost)
  grossIncome?: number; // Gross Income (revenue - manpowerCost)
  grossIncomeRate?: number; // Gross Income %
  operationIncome?: number; // Operation Income
  operationIncomeRate?: number; // Operation Income %
  incomeTax?: number; // Income Tax (Estimated) 20%
  profit?: number; // Profit
  profitRate?: number; // Profit %

  // 추가 계산 데이터
  ecm?: number; // ECM (Gross Income * (0.45/1.45))
  costOfFinance?: number; // Cost of Finance (manpowerCost * costOfFinanceRate)
  costOfFinanceRate?: number; // Cost of Finance 비율 (기본값 0.1 = 10%)
  projectManagementRate?: number; // Project Management 비율 (기본값 0.03 = 3%)
  reserved?: number; // Reserved (roundup(Cost of Finance + ECM, -2))

  // 프로젝트 기본 정보
  months?: number; // 프로젝트 기간(월)

  // JSON 필드로 세부 데이터 저장
  revenueItems?: any; // 매출 세부 항목
  grossCostItems?: any; // Gross Cost 세부 항목
  manpowerItems?: any; // 투입인건비 세부 항목
  indirectOpexItems?: any; // Indirect OPEX 세부 항목
  directOpexItems?: any; // Direct OPEX 세부 항목
  revenueByConsultantItems?: any; // Revenue by Consultant 세부 항목
}

export interface UpdatePPEDto extends Partial<CreatePPEDto> {}

export interface EmployeeInputSummary {
  employeeName: string;
  totalMM: number;
  totalAmount: number;
  projects: Array<{
    projectName: string;
    mm: number;
    amount: number;
    monthlyInputs: number[];
  }>;
}

export interface EmployeeInputDetails {
  employeeName: string;
  totalMM: number;
  totalAmount: number;
  monthlyDetails: Array<{
    month: number;
    totalInput: number;
    projects: Array<{
      projectName: string;
      input: number;
    }>;
  }>;
  projectDetails: Array<{
    projectName: string;
    projectPeriod: string;
    mm: number;
    amount: number;
    monthlyInputs: number[];
  }>;
}

export const ppeApi = {
  // 모든 PPE 조회
  getAll: (): Promise<PPEData[]> => {
    return apiClient.get<PPEData[]>('/ppe');
  },

  // 프로젝트별 PPE 조회
  getByProjectId: (projectId: number): Promise<PPEData> => {
    return apiClient.get<PPEData>(`/ppe/project/${projectId}`);
  },

  // PPE 생성
  create: (projectId: number, data: CreatePPEDto): Promise<PPEData> => {
    return apiClient.post<PPEData>(`/ppe/project/${projectId}`, data);
  },

  // PPE 업데이트
  update: (id: number, data: UpdatePPEDto): Promise<PPEData> => {
    return apiClient.patch<PPEData>(`/ppe/${id}`, data);
  },

  // PPE 삭제
  delete: (id: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/ppe/${id}`);
  },

  // 직원별 투입률 집계
  getEmployeeInputSummary: (startDate?: string, endDate?: string): Promise<EmployeeInputSummary[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const url = queryString ? `/ppe/employee-inputs/summary?${queryString}` : '/ppe/employee-inputs/summary';
    
    return apiClient.get<EmployeeInputSummary[]>(url);
  },

  // 특정 직원 투입 상세 내역
  getEmployeeInputDetails: (employeeName: string): Promise<EmployeeInputDetails> => {
    return apiClient.get<EmployeeInputDetails>(`/ppe/employee-inputs/${encodeURIComponent(employeeName)}/details`);
  },
};